import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  ActiveElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartEvent,
  ArcElement,
  Legend,
  LinearScale,
  Title,
  Tooltip,
  Plugin
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import {
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Loader,
  Modal,
  NumberInput,
  Skeleton,
  Stack,
  Table,
  Tabs,
  Text,
  Breadcrumbs
} from '@mantine/core';
import Card from 'components/Card';
import DataTables, { DataTableColumn } from 'components/DataTables';
import { useQuery, useQueries } from '@tanstack/react-query';
import { masterPlanService } from 'services/dacum/masterPlan.service';
import {
  MasterPlanBranchedItem,
  MasterPlanBranchedCompetency,
  MasterPlanBranchedParams,
  MasterPlanBranchedResponse,
  MasterPlanOverviewCount,
  MasterPlanOverviewCategory,
  MasterPlanOverviewResponse,
  MasterPlanOverviewCourseResponse,
  MasterPlanOverviewCourseStatus,
  MasterPlanOverviewCourseStatusBucket,
  MasterPlanOverviewCourseCompetency
} from 'types/dacum/masterPlanOverview';
import { showErrorNotification } from 'utils/notification';
import { useNavigate } from 'react-router-dom';
import { pathnames } from 'routes/dacum';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

type CategoryConfig = {
  label: string;
  color: string;
  flagKey: keyof Omit<MasterPlanBranchedParams, 'schoolName' | 'year' | 'jobBand'>;
};

const categoryMap: Record<MasterPlanOverviewCategory, CategoryConfig> = {
  new: {
    label: 'New',
    color: '#42A5F5',
    flagKey: 'isNew'
  },
  active: {
    label: 'Active',
    color: '#4CAF50',
    flagKey: 'isActive'
  },
  inRevision: {
    label: 'In Revision',
    color: '#FFA000',
    flagKey: 'isInRevision'
  },
  expired: {
    label: 'Expired',
    color: '#F3352E',
    flagKey: 'isExpired'
  },
  archived: {
    label: 'Archived',
    color: '#9AA1B1',
    flagKey: 'isArchived'
  },
  rejected: {
    label: 'Rejected',
    color: '#D14343',
    flagKey: 'isRejected'
  }
};

const categoryOrder: MasterPlanOverviewCategory[] = [
  'new',
  'active',
  'inRevision',
  'expired',
  'archived',
  'rejected'
];

const getCountByCategory = (
  item: { listCount?: MasterPlanOverviewCount[] },
  category: MasterPlanOverviewCategory
) => item.listCount?.find((entry) => entry.category === category)?.count ?? 0;

// Convert a hex color to rgba with given alpha
const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const barCenterLabelPlugin: Plugin<'bar'> = {
  id: 'barCenterLabel',
  afterDatasetsDraw: (chart) => {
    const { ctx } = chart;
    ctx.save();

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);

      meta.data.forEach((element, index) => {
        const rawValue = dataset.data?.[index] as number | undefined;
        if (rawValue === undefined || rawValue === null || rawValue <= 0) return;

        const { x, y, base } = element.getProps(['x', 'y', 'base'], true);
        if (x === null || y === null || !Number.isFinite(x) || !Number.isFinite(y)) return;
        const baseValue = typeof base === 'number' ? base : y;
        const barHeight = Math.abs(baseValue - y);
        const centerY = y + (baseValue - y) / 2;
        const isRoomInside = barHeight >= 24;

        const label = String(rawValue);
        ctx.font = '600 12px "Inter", -apple-system, system-ui, sans-serif';
        const textMetrics = ctx.measureText(label);
        const textWidth = textMetrics.width;
        const textHeight = 12; // approximate since measureText does not return height
        const paddingX = 6;
        const paddingY = 4;
        const boxWidth = textWidth + paddingX * 2;
        const boxHeight = textHeight + paddingY * 2;
        const boxX = x - boxWidth / 2;
        const boxY = isRoomInside ? centerY - boxHeight / 2 : y - boxHeight - 6;

        const drawRoundedRect = (rectX: number, rectY: number, width: number, height: number) => {
          const radius = 6;
          ctx.beginPath();
          ctx.moveTo(rectX + radius, rectY);
          ctx.lineTo(rectX + width - radius, rectY);
          ctx.quadraticCurveTo(rectX + width, rectY, rectX + width, rectY + radius);
          ctx.lineTo(rectX + width, rectY + height - radius);
          ctx.quadraticCurveTo(
            rectX + width,
            rectY + height,
            rectX + width - radius,
            rectY + height
          );
          ctx.lineTo(rectX + radius, rectY + height);
          ctx.quadraticCurveTo(rectX, rectY + height, rectX, rectY + height - radius);
          ctx.lineTo(rectX, rectY + radius);
          ctx.quadraticCurveTo(rectX, rectY, rectX + radius, rectY);
          ctx.closePath();
        };

        ctx.fillStyle = '#111827E6';
        drawRoundedRect(boxX, boxY, boxWidth, boxHeight);
        ctx.fill();

        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, x, boxY + boxHeight / 2);
      });
    });

    ctx.restore();
  }
};

const pieSliceLabelPlugin: Plugin<'pie'> = {
  id: 'pieSliceLabel',
  afterDatasetsDraw: (chart) => {
    const { ctx } = chart;
    const dataset = chart.data.datasets?.[0];
    if (!dataset) return;

    ctx.save();
    ctx.font = '600 12px "Inter", -apple-system, system-ui, sans-serif';

    const meta = chart.getDatasetMeta(0);
    meta.data.forEach((element, index) => {
      const rawValue = dataset.data?.[index] as number | undefined;
      if (rawValue === undefined || rawValue === null || rawValue <= 0) return;

      const { x, y } = element.tooltipPosition(true);
      if (x === null || y === null || !Number.isFinite(x) || !Number.isFinite(y)) return;
      const label = String(rawValue);
      const paddingX = 6;
      const paddingY = 4;
      const textWidth = ctx.measureText(label).width;
      const textHeight = 12;
      const boxWidth = textWidth + paddingX * 2;
      const boxHeight = textHeight + paddingY * 2;
      const boxX = x - boxWidth / 2;
      const boxY = y - boxHeight / 2;

      const drawRoundedRect = (rectX: number, rectY: number, width: number, height: number) => {
        const radius = 6;
        ctx.beginPath();
        ctx.moveTo(rectX + radius, rectY);
        ctx.lineTo(rectX + width - radius, rectY);
        ctx.quadraticCurveTo(rectX + width, rectY, rectX + width, rectY + radius);
        ctx.lineTo(rectX + width, rectY + height - radius);
        ctx.quadraticCurveTo(rectX + width, rectY + height, rectX + width - radius, rectY + height);
        ctx.lineTo(rectX + radius, rectY + height);
        ctx.quadraticCurveTo(rectX, rectY + height, rectX, rectY + height - radius);
        ctx.lineTo(rectX, rectY + radius);
        ctx.quadraticCurveTo(rectX, rectY, rectX + radius, rectY);
        ctx.closePath();
      };

      ctx.fillStyle = '#111827E6';
      drawRoundedRect(boxX, boxY, boxWidth, boxHeight);
      ctx.fill();

      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, x, y);
    });

    ctx.restore();
  }
};

const buildYearLabel = (year?: number, endYear?: number | null) => {
  if (!year && !endYear) return '-';
  if (year && !endYear) return `${year}`;
  if (!year && endYear) return `${endYear}`;
  return `${year} - ${endYear}`;
};

const MasterplanOverview: React.FC = () => {
  type MasterplanSummaryItem = { label: string; color: string; count: number };

  const [tab, setTab] = useState<'competencies' | 'courses'>('competencies');
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [selectedSchool, setSelectedSchool] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<MasterPlanOverviewCategory | null>(null);
  const [competencyModalOpen, setCompetencyModalOpen] = useState(false);
  const [selectedCompetencies, setSelectedCompetencies] = useState<MasterPlanBranchedCompetency[]>(
    []
  );
  const [selectedRowMeta, setSelectedRowMeta] = useState<{
    school?: string;
    jobFamGroup?: string | null;
  } | null>(null);
  const [selectedMsSchoolCompetencyManagementIDs, setSelectedMsSchoolCompetencyManagementIDs] =
    useState<string[]>([]);
  const [selectedCourseStatus, setSelectedCourseStatus] =
    useState<MasterPlanOverviewCourseStatus | null>(null);
  const [courseCompetencyModalOpen, setCourseCompetencyModalOpen] = useState(false);
  const [selectedCourseCompetencies, setSelectedCourseCompetencies] = useState<
    MasterPlanOverviewCourseCompetency[]
  >([]);

  const navigate = useNavigate();

  const handleViewMasterplan = useCallback(
    (mp: { jobFamGroup: string | null; startYear: number | null; endYear: number | null }) => {
      const queryParams = new URLSearchParams();
      queryParams.append('jobFamilyGroup', mp.jobFamGroup || '');
      if (mp.startYear) queryParams.append('year', mp.startYear.toString());
      if (mp.endYear) queryParams.append('endYear', mp.endYear.toString());
      navigate(`${pathnames.masterPlanDetail}?${queryParams.toString()}`);
    },
    [navigate]
  );

  const masterplanChartRef = useRef<ChartJS<'bar'>>(null);
  const courseChartRef = useRef<ChartJS<'bar' | 'pie'>>(null);

  const mapMasterplanStatus = useCallback((value?: string | null) => {
    const normalized = value?.trim().toLowerCase();
    if (normalized === 'new') return { label: 'New', color: '#42A5F5' };
    if (!normalized) return { label: 'Pending', color: 'gray' };
    if (normalized === 'approved' || normalized === 'approve') {
      return { label: 'Approved', color: 'green' };
    }
    if (normalized === 'rejected' || normalized === 'reject') {
      return { label: 'Rejected', color: 'red' };
    }
    return { label: value || 'Pending', color: 'gray' };
  }, []);

  const buildSummaryMasterplan = useCallback(
    (competencies: MasterPlanOverviewCourseCompetency[] | null | undefined) => {
      const map = new Map<string, MasterplanSummaryItem>();

      (competencies || []).forEach((competency) => {
        (competency.listMasterplan || []).forEach((mp) => {
          const status = mapMasterplanStatus(mp.statusMasterplan);
          const existing = map.get(status.label);
          if (existing) {
            existing.count += 1;
          } else {
            map.set(status.label, { label: status.label, color: status.color, count: 1 });
          }
        });
      });

      return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    },
    [mapMasterplanStatus]
  );

  const resetMasterplanFilters = useCallback(() => {
    setSelectedSchool(null);
    setSelectedCategory(null);
    setSelectedRowMeta(null);
    setSelectedCompetencies([]);
    setCompetencyModalOpen(false);
    setSelectedMsSchoolCompetencyManagementIDs([]);
  }, []);

  const resetStatus = useCallback(() => setSelectedCategory(null), []);

  const handleYearChange = (value: string | number) => {
    if (typeof value !== 'number') return;
    setYear(value);
    resetMasterplanFilters();
    setSelectedCourseStatus(null);
    setCourseCompetencyModalOpen(false);
    setSelectedCourseCompetencies([]);
  };

  const {
    data: overviewResponse,
    isPending: overviewLoading,
    error: overviewError
  } = useQuery<MasterPlanOverviewResponse>({
    queryKey: ['masterplan-overview', year],
    queryFn: () => masterPlanService.getMasterPlanOverview({ year })
  });

  useEffect(() => {
    if (overviewError) {
      showErrorNotification({
        message: overviewError.message || 'Failed to load masterplan overview'
      });
    }
  }, [overviewError]);

  const overviewData = Array.isArray(overviewResponse?.data) ? overviewResponse.data : [];

  const {
    data: courseOverviewResponse,
    isPending: courseOverviewLoading,
    error: courseOverviewError
  } = useQuery<MasterPlanOverviewCourseResponse>({
    queryKey: ['masterplan-course-overview', year],
    queryFn: () => masterPlanService.getMasterPlanCourseOverview({ year })
  });

  useEffect(() => {
    if (courseOverviewError) {
      showErrorNotification({
        message: courseOverviewError.message || 'Failed to load course overview'
      });
    }
  }, [courseOverviewError]);

  const courseOverviewBuckets: MasterPlanOverviewCourseStatusBucket[] = Array.isArray(
    courseOverviewResponse?.data
  )
    ? courseOverviewResponse?.data || []
    : [];

  const masterplanChartSource = useMemo(() => {
    const entries: {
      key: string;
      schoolName: string;
      msSchoolCompetencyManagementID: string | undefined;
      businessUnitID: string | undefined;
      businessUnitName: string | null | undefined;
      description: string | undefined;
      jobFamilyGroupID: string | undefined;
      listCount: MasterPlanOverviewCount[];
    }[] = [];

    overviewData.forEach((item) => {
      const schoolName = item.schoolName?.trim() || 'Unknown School';
      const id = item.msSchoolCompetencyManagementID;
      const key = id || schoolName;

      const existing = entries.find((e) => e.key === key);
      if (existing) {
        (item.listCount || []).forEach((countItem) => {
          const existingCount = existing.listCount.find((c) => c.category === countItem.category);
          if (existingCount) {
            existingCount.count += countItem.count;
          } else {
            existing.listCount.push({ category: countItem.category, count: countItem.count });
          }
        });
      } else {
        entries.push({
          key,
          schoolName,
          msSchoolCompetencyManagementID: id,
          businessUnitID: item.businessUnitID,
          businessUnitName: item.businessUnitName,
          description: item.description,
          jobFamilyGroupID: item.jobFamilyGroupID,
          listCount: [...(item.listCount || [])]
        });
      }
    });

    return entries.map((e) => ({
      key: e.key,
      schoolName: e.schoolName,
      msSchoolCompetencyManagementID: e.msSchoolCompetencyManagementID,
      businessUnitID: e.businessUnitID,
      businessUnitName: e.businessUnitName,
      description: e.description,
      jobFamilyGroupID: e.jobFamilyGroupID,
      listCount: e.listCount
    }));
  }, [overviewData]);

  const stackedChartMeta = useMemo(() => {
    const schoolNames: string[] = [];
    masterplanChartSource.forEach((e) => {
      if (!schoolNames.includes(e.schoolName)) schoolNames.push(e.schoolName);
    });

    // Slots: (school × category), only where the school has data for that category
    const slots: { schoolName: string; category: MasterPlanOverviewCategory }[] = [];
    schoolNames.forEach((schoolName) => {
      categoryOrder.forEach((category) => {
        const hasData = masterplanChartSource.some(
          (e) => e.schoolName === schoolName && getCountByCategory(e, category) > 0
        );
        if (hasData) slots.push({ schoolName, category });
      });
    });

    // Fallback: if all zeros still show all slots so chart renders
    const effectiveSlots =
      slots.length > 0
        ? slots
        : schoolNames.flatMap((schoolName) =>
            categoryOrder.map((category) => ({ schoolName, category }))
          );

    const slotLabels = effectiveSlots.map((s) => categoryMap[s.category].label);

    const buKeys: string[] = [];
    const buKeyToLabel = new Map<string, string>();
    const buKeyToMsIDs = new Map<string, string[]>();
    masterplanChartSource.forEach((e) => {
      const key =
        e.businessUnitID || e.businessUnitName || e.msSchoolCompetencyManagementID || e.key;
      if (!key) return;
      if (!buKeys.includes(key)) {
        buKeys.push(key);
        buKeyToLabel.set(
          key,
          e.businessUnitName || e.businessUnitID || e.description || e.jobFamilyGroupID || key
        );
        buKeyToMsIDs.set(key, []);
      }
      if (e.msSchoolCompetencyManagementID) {
        buKeyToMsIDs.get(key)!.push(e.msSchoolCompetencyManagementID);
      }
    });

    return { schoolNames, slots: effectiveSlots, slotLabels, buKeys, buKeyToLabel, buKeyToMsIDs };
  }, [masterplanChartSource]);

  const groupedSchoolLabelPlugin = useMemo<Plugin<'bar'>>(
    () => ({
      id: 'groupedSchoolLabel',
      afterDraw: (chart) => {
        const { slots } = stackedChartMeta;
        if (!slots.length) return;
        const xAxis = chart.scales['x'];
        if (!xAxis) return;

        const groups: { schoolName: string; start: number; end: number }[] = [];
        slots.forEach((slot, idx) => {
          const last = groups[groups.length - 1];
          if (last && last.schoolName === slot.schoolName) {
            last.end = idx;
          } else {
            groups.push({ schoolName: slot.schoolName, start: idx, end: idx });
          }
        });

        const { ctx } = chart;
        ctx.save();

        const bracketY = xAxis.bottom + 6;
        const labelY = bracketY + 14;
        const pad = 10;

        groups.forEach(({ schoolName, start, end }) => {
          const x1 = xAxis.getPixelForTick(start) - pad;
          const x2 = xAxis.getPixelForTick(end) + pad;
          const midX = (x1 + x2) / 2;

          ctx.beginPath();
          ctx.strokeStyle = '#9CA3AF';
          ctx.lineWidth = 1;
          ctx.moveTo(x1, bracketY - 4);
          ctx.lineTo(x1, bracketY);
          ctx.lineTo(x2, bracketY);
          ctx.lineTo(x2, bracketY - 4);
          ctx.stroke();

          ctx.font = '600 11px "Inter", -apple-system, system-ui, sans-serif';
          ctx.fillStyle = '#374151';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          ctx.fillText(schoolName, midX, labelY);
        });

        ctx.restore();
      }
    }),
    [stackedChartMeta]
  );

  const chartData = useMemo(() => {
    const { slots, slotLabels, buKeys, buKeyToLabel } = stackedChartMeta;
    const hasSelection = Boolean(selectedSchool);

    const datasets = buKeys.map((buKey, buIdx) => {
      const normalAlpha = Math.max(1 - buIdx * 0.2, 0.4);
      const dimmedAlpha = 0.15;

      return {
        label: `__${buKeyToLabel.get(buKey) || buKey}`,
        data: slots.map((slot) => {
          const matchingEntries = masterplanChartSource.filter((e) => {
            const entryBuKey =
              e.businessUnitID || e.businessUnitName || e.msSchoolCompetencyManagementID || e.key;
            return e.schoolName === slot.schoolName && entryBuKey === buKey;
          });
          return matchingEntries.reduce((sum, e) => sum + getCountByCategory(e, slot.category), 0);
        }),
        backgroundColor: slots.map((slot) => {
          const baseColor = categoryMap[slot.category].color;
          const isActiveSlot =
            (!hasSelection || selectedSchool === slot.schoolName) &&
            (!selectedCategory || selectedCategory === slot.category);
          return isActiveSlot
            ? hexToRgba(baseColor, normalAlpha)
            : hexToRgba(baseColor, dimmedAlpha);
        }),
        borderColor: slots.map((slot) => {
          const baseColor = categoryMap[slot.category].color;
          const isActive =
            hasSelection &&
            selectedSchool === slot.schoolName &&
            (!selectedCategory || selectedCategory === slot.category);
          return isActive ? baseColor : 'transparent';
        }),
        borderWidth: slots.map((slot) => {
          const isActive =
            hasSelection &&
            selectedSchool === slot.schoolName &&
            (!selectedCategory || selectedCategory === slot.category);
          return isActive ? 2 : 0;
        }),
        borderRadius: 4,
        maxBarThickness: 40,
        stack: 'main' as const
      };
    });

    return { labels: slotLabels, datasets, datasetMeta: slots };
  }, [stackedChartMeta, masterplanChartSource, selectedSchool, selectedCategory]);

  const masterplanChartLoading = overviewLoading;
  const masterplanChartHasData = Boolean(chartData.labels?.length);

  const masterplanBreadcrumbs = useMemo(() => {
    const crumbs = [] as {
      label: string;
      onClick: () => void;
      disabled?: boolean;
    }[];

    crumbs.push({
      label: selectedSchool ? 'All schools' : 'All schools',
      onClick: resetMasterplanFilters,
      disabled: !selectedSchool && !selectedCategory
    });

    crumbs.push({
      label: selectedSchool ? `School: ${selectedSchool}` : 'Pick a school',
      onClick: resetMasterplanFilters,
      disabled: !selectedSchool
    });

    crumbs.push({
      label: selectedSchool
        ? selectedCategory
          ? `Status: ${categoryMap[selectedCategory].label}`
          : 'All statuses'
        : 'Pick a school',
      onClick: resetStatus,
      disabled: !selectedSchool
    });

    return crumbs;
  }, [resetMasterplanFilters, resetStatus, selectedCategory, selectedSchool]);

  const courseStatusOrder: MasterPlanOverviewCourseStatus[] = useMemo(
    () => ['New', 'Active', 'In Revision', 'Expired', 'Archived'],
    []
  );

  const courseStatusColorMap: Record<MasterPlanOverviewCourseStatus, string> = {
    New: '#42A5F5',
    Active: '#4CAF50',
    'In Revision': '#FFA000',
    Expired: '#F3352E',
    Archived: '#9AA1B1'
  };

  const courseChartData = useMemo(() => {
    if (!courseOverviewBuckets.length) return { labels: [], datasets: [] };

    const labels = courseStatusOrder;
    const data = labels.map((status) => {
      const bucket = courseOverviewBuckets.find((item) => item.status === status);
      return bucket?.count || 0;
    });

    return {
      labels,
      datasets: [
        {
          label: 'Courses',
          data,
          backgroundColor: labels.map((status) => courseStatusColorMap[status]),
          borderColor: '#fff',
          borderWidth: 1
        }
      ]
    };
  }, [courseOverviewBuckets, courseStatusOrder]);

  const selectedFlagKey = selectedCategory ? categoryMap[selectedCategory].flagKey : null;

  // Derive the effective set of msSchoolCompetencyManagementIDs to query:
  // - If specific IDs are selected (e.g. from clicking a bar segment), use those.
  // - Otherwise, derive all IDs for the selected school+category from the chart source,
  //   so clicking a status/legend loops all relevant BUs instead of fetching without an ID.
  const effectiveIDs = useMemo(() => {
    if (!selectedSchool) return [];
    if (selectedMsSchoolCompetencyManagementIDs.length > 0) {
      return selectedMsSchoolCompetencyManagementIDs;
    }
    return masterplanChartSource
      .filter(
        (e) =>
          e.schoolName === selectedSchool &&
          e.msSchoolCompetencyManagementID &&
          (!selectedCategory || getCountByCategory(e, selectedCategory) > 0)
      )
      .map((e) => e.msSchoolCompetencyManagementID!);
  }, [
    selectedSchool,
    selectedMsSchoolCompetencyManagementIDs,
    masterplanChartSource,
    selectedCategory
  ]);

  const branchedQueryResults = useQueries({
    queries: effectiveIDs.map((id) => ({
      queryKey: ['masterplan-overview-detail', selectedSchool, selectedCategory, year, id] as const,
      queryFn: () =>
        masterPlanService.getMasterPlanBranchedOverview({
          schoolName: selectedSchool || '',
          year,
          ...(selectedFlagKey ? { [selectedFlagKey]: true } : {}),
          msSchoolCompetencyManagementID: id
        }),
      enabled: Boolean(selectedSchool) && Boolean(id)
    }))
  });

  const branchedLoading = branchedQueryResults.some((q) => q.isFetching);
  const branchedError = branchedQueryResults.find((q) => q.error)?.error ?? null;
  const branchedData = useMemo(
    () =>
      branchedQueryResults.flatMap(
        (q) => (q.data as MasterPlanBranchedResponse | undefined)?.data ?? []
      ),
    [branchedQueryResults]
  );

  useEffect(() => {
    if (branchedError) {
      showErrorNotification({
        message: (branchedError as Error).message || 'Failed to load branched masterplan'
      });
    }
  }, [branchedError]);

  const detailRows = useMemo(() => {
    if (!branchedData.length) return [] as any[];

    return branchedData.map((item: MasterPlanBranchedItem) => {
      // Derive business unit names from listCompetency (unique, comma-separated)
      const businessUnitNames = [
        ...new Set((item.listCompetency || []).map((c) => c.businessUnitName).filter(Boolean))
      ].join(', ');

      // Normalise status using categoryMap so "new" → "New", "inRevision" → "In Revision", etc.
      // Fall back to selectedCategory when the API returns an empty/null status field.
      const rawStatus =
        item.status || (selectedCategory ? categoryMap[selectedCategory].label : '') || '';
      const statusLabel =
        Object.values(categoryMap).find(
          (cfg) => cfg.label.toLowerCase() === rawStatus.toLowerCase()
        )?.label ||
        Object.entries(categoryMap).find(
          ([key]) => key.toLowerCase() === rawStatus.toLowerCase()
        )?.[1].label ||
        rawStatus ||
        '-';

      return {
        jobFamGroup: item.jobFamGroup || '-',
        yearRange: buildYearLabel(item.year, item.endYear),
        status: statusLabel,
        schoolName: item.schoolName,
        schoolId: item.schoolID,
        businessUnit: businessUnitNames || '-',
        totalCompetency: item.listCompetency?.length || 0,
        listCompetency: item.listCompetency || []
      };
    });
  }, [branchedData, selectedCategory, masterplanChartSource]);

  const detailColumns = useMemo<DataTableColumn[]>(
    () => [
      {
        heading: 'School',
        props: 'schoolName',
        sortable: true,
        id: 'schoolName',
        active: true
      },
      {
        heading: 'Business Unit',
        props: 'businessUnit',
        sortable: true,
        id: 'businessUnit',
        active: true
      },
      {
        heading: 'Job Family Group',
        props: (row: any) => row.jobFamGroup || '-',
        sortable: true,
        id: 'jobFamGroup',
        active: true
      },
      {
        heading: 'Year Range',
        props: 'yearRange',
        sortable: true,
        id: 'yearRange',
        active: true
      },
      {
        heading: 'Status',
        props: 'status',
        sortable: true,
        id: 'status',
        active: true
      },
      {
        heading: 'Total Competency',
        props: 'totalCompetency',
        sortable: true,
        id: 'totalCompetency',
        active: true
      },
      {
        heading: 'Actions',
        props: (row: any) => (
          <Button
            size='xs'
            variant='light'
            disabled={!row.listCompetency || row.listCompetency.length === 0}
            onClick={() => {
              setSelectedCompetencies(row.listCompetency || []);
              setSelectedRowMeta({ school: row.schoolName, jobFamGroup: row.jobFamGroup });
              setCompetencyModalOpen(true);
            }}
          >
            View Competencies
          </Button>
        ),
        sortable: false,
        active: true
      }
    ],
    []
  );

  const courseRows = useMemo(() => {
    if (!selectedCourseStatus) return [];
    const bucket = courseOverviewBuckets.find((item) => item.status === selectedCourseStatus);
    if (!bucket?.listCourse?.length) return [];

    return bucket.listCourse.map((course) => ({
      schoolName: course.schoolName || '-',
      statusLabel: selectedCourseStatus,
      courseName: course.courseName,
      competencyName: course.competencyName,
      jobFamGroup: course.jobFamGroup || '-',
      jobFam: course.jobFam || '-',
      jobBand: course.jobBand || '-',
      masterplanName: course.masterplanName || '-',
      statusMasterplanLabel: mapMasterplanStatus(course.statusMasterplan).label,
      statusMasterplanColor: mapMasterplanStatus(course.statusMasterplan).color,
      expiredDate: course.expiredDate || '-',
      summaryMasterplan: buildSummaryMasterplan(course.competencyName)
    }));
  }, [buildSummaryMasterplan, courseOverviewBuckets, selectedCourseStatus]);

  const groupedCourseRows = useMemo(() => {
    const grouped = new Map<string, typeof courseRows>();
    courseRows.forEach((row) => {
      const key = row.courseName || 'Unknown';
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(row);
    });
    return Array.from(grouped.entries()).map(([courseName, rows]) => ({
      courseName,
      rows
    }));
  }, [courseRows]);

  const handleBarClick = (event: ChartEvent, elements: ActiveElement[]) => {
    const chart = masterplanChartRef.current;
    if (!chart) return;

    let slotIndex: number | undefined;

    if (elements.length > 0) {
      slotIndex = elements[0].index;
    } else {
      // Detect click on x-axis tick / bracket label area
      const xAxis = chart.scales['x'];
      const chartArea = chart.chartArea;
      const mouseX = (event as any).x as number;
      const mouseY = (event as any).y as number;
      if (
        mouseX != null &&
        mouseY != null &&
        mouseY > chartArea.bottom &&
        mouseY < chartArea.bottom + 80 &&
        mouseX >= xAxis.left &&
        mouseX <= xAxis.right &&
        xAxis
      ) {
        // Find the nearest tick by pixel position
        const numTicks = xAxis.ticks.length;
        let nearest = 0;
        let nearestDist = Infinity;
        for (let i = 0; i < numTicks; i++) {
          const px = xAxis.getPixelForValue(i);
          const dist = Math.abs(px - mouseX);
          if (dist < nearestDist) {
            nearestDist = dist;
            nearest = i;
          }
        }
        // Only accept if within half a tick-step of the tick centre
        const halfStep = numTicks > 1 ? xAxis.width / numTicks / 2 : xAxis.width / 2;
        if (nearestDist <= halfStep) {
          slotIndex = nearest;
        }
      }
    }

    if (slotIndex === undefined || slotIndex < 0) return;

    const slot = chartData.datasetMeta?.[slotIndex];
    if (!slot) return;

    // When a specific bar segment is clicked, capture its msSchoolCompetencyManagementID;
    // when clicking the x-axis tick/label area, reset to let effectiveIDs auto-derive all
    // relevant IDs for that school+category.
    if (elements.length > 0) {
      const buKey = stackedChartMeta.buKeys[elements[0].datasetIndex];
      const msIDs = buKey ? (stackedChartMeta.buKeyToMsIDs.get(buKey) ?? []) : [];
      setSelectedMsSchoolCompetencyManagementIDs(msIDs.length > 0 ? msIDs : buKey ? [buKey] : []);
    } else {
      setSelectedMsSchoolCompetencyManagementIDs([]);
    }
    setSelectedSchool(slot.schoolName);
    setSelectedCategory(slot.category);
    setSelectedCompetencies([]);
    setSelectedRowMeta(null);
    setCompetencyModalOpen(false);
  };

  const handleCourseBarClick = (event: ChartEvent, elements: ActiveElement[]) => {
    const chart = courseChartRef.current;
    if (!chart) return;
    const primaryElement = elements?.[0];
    const fallbackPoints = chart.getElementsAtEventForMode(
      event as any,
      'nearest',
      { intersect: true },
      true
    );

    const index = primaryElement?.index ?? fallbackPoints?.[0]?.index;
    if (index === undefined || index === null) return;

    const status = courseChartData.labels?.[index] as MasterPlanOverviewCourseStatus | undefined;
    if (!status) return;

    setSelectedCourseStatus(status);
  };

  const resetSelection = () => {
    resetMasterplanFilters();
    setSelectedCourseStatus(null);
    setCourseCompetencyModalOpen(false);
    setSelectedCourseCompetencies([]);
  };

  const modalTitle = `Competencies${
    selectedRowMeta?.school ? ` · ${selectedRowMeta.school}` : ''
  }${selectedRowMeta?.jobFamGroup ? ` · ${selectedRowMeta.jobFamGroup}` : ''}`;

  const courseCompetencyModalTitle = 'Course Masterplan';

  return (
    <Stack gap='md'>
      <Card>
        <Stack gap='md'>
          <Flex justify='space-between' align='center' gap='md'>
            <Group gap='xs'>
              <Text fw={600}>Year</Text>
              <NumberInput
                value={year}
                min={2000}
                max={2100}
                clampBehavior='strict'
                onChange={handleYearChange}
                maw={120}
              />
            </Group>
            <Group gap='xs'>
              <Badge variant='light'>Overview data for {year}</Badge>
              {tab === 'competencies' && selectedSchool && (
                <Badge color='gray' variant='filled' c='white'>
                  {selectedSchool}
                  {selectedCategory
                    ? ` · ${categoryMap[selectedCategory].label}`
                    : ' · All statuses'}
                </Badge>
              )}

              <Button
                variant='subtle'
                size='xs'
                onClick={resetSelection}
                disabled={!selectedSchool && !selectedCategory && !selectedCourseStatus}
              >
                Clear selection
              </Button>
            </Group>
          </Flex>

          <Tabs
            value={tab}
            onChange={(value) => setTab((value as 'competencies' | 'courses') || 'competencies')}
            keepMounted={false}
          >
            <Tabs.List>
              <Tabs.Tab value='competencies'>Masterplan</Tabs.Tab>
              <Tabs.Tab value='courses'>Course</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value='competencies' pt='md'>
              <Stack gap='md'>
                <Text c='dimmed' fz='sm'>
                  Click any school bar to view detailed branched masterplans for that status.
                </Text>
                <Breadcrumbs separator='›'>
                  {masterplanBreadcrumbs.map((crumb, index) => (
                    <Button
                      key={`${crumb.label}-${index}`}
                      size='xs'
                      variant='subtle'
                      color='blue'
                      disabled={crumb.disabled}
                      onClick={crumb.onClick}
                    >
                      {crumb.label}
                    </Button>
                  ))}
                </Breadcrumbs>
                <Stack gap='xs'>
                  <Text fz='sm' fw={600}>
                    School Filter
                  </Text>
                  <Group gap='xs' wrap='wrap'>
                    <Button
                      size='xs'
                      variant={!selectedSchool ? 'filled' : 'light'}
                      onClick={() => {
                        setSelectedSchool(null);
                        setSelectedCategory(null);
                        setSelectedRowMeta(null);
                        setSelectedCompetencies([]);
                        setCompetencyModalOpen(false);
                        setSelectedMsSchoolCompetencyManagementIDs([]);
                      }}
                    >
                      All schools
                    </Button>
                    {stackedChartMeta.schoolNames.map((schoolName, idx) => {
                      const isActive = selectedSchool === schoolName;
                      return (
                        <Button
                          key={`school-filter-${idx}`}
                          size='xs'
                          variant={isActive ? 'filled' : 'light'}
                          color={isActive ? 'blue' : 'gray'}
                          onClick={() => {
                            setSelectedMsSchoolCompetencyManagementIDs([]);
                            setSelectedSchool(schoolName);
                            setSelectedCategory(null);
                            setSelectedRowMeta(null);
                            setSelectedCompetencies([]);
                            setCompetencyModalOpen(false);
                          }}
                        >
                          {schoolName}
                        </Button>
                      );
                    })}
                  </Group>
                </Stack>
                <Box h={360}>
                  {masterplanChartLoading ? (
                    <Flex justify='center' align='center' h='100%'>
                      <Loader />
                    </Flex>
                  ) : masterplanChartHasData ? (
                    <Bar
                      ref={masterplanChartRef}
                      data={chartData as any}
                      plugins={[barCenterLabelPlugin, groupedSchoolLabelPlugin]}
                      options={{
                        onClick: (event, elements) =>
                          handleBarClick(event as ChartEvent, elements as ActiveElement[]),
                        onHover: (event, elements, chart) => {
                          const canvas = chart.canvas;
                          if (!canvas) return;
                          const mouseX = (event as any).x as number;
                          const mouseY = (event as any).y as number;
                          const chartArea = chart.chartArea;
                          const xAxis = chart.scales['x'];
                          const inTickArea =
                            mouseX != null &&
                            mouseY != null &&
                            mouseY > chartArea.bottom &&
                            mouseY < chartArea.bottom + 80 &&
                            mouseX >= xAxis?.left &&
                            mouseX <= xAxis?.right;
                          canvas.style.cursor =
                            elements.length > 0 || inTickArea ? 'pointer' : 'default';
                        },
                        responsive: true,
                        maintainAspectRatio: false,
                        layout: {
                          padding: { bottom: 44 }
                        },
                        plugins: {
                          legend: {
                            position: 'top',
                            labels: {
                              generateLabels: () =>
                                categoryOrder.map((cat) => ({
                                  text: categoryMap[cat].label,
                                  fillStyle: categoryMap[cat].color,
                                  strokeStyle: 'transparent',
                                  lineWidth: 0,
                                  hidden: false,
                                  datasetIndex: -1,
                                  index: 0
                                }))
                            },
                            onClick: (_e, legendItem) => {
                              const cat = categoryOrder.find(
                                (c) => categoryMap[c].label === legendItem.text
                              );
                              if (!cat) return;
                              setSelectedCategory((prev) => (prev === cat ? null : cat));
                              // Reset specific IDs so effectiveIDs re-derives from school+category
                              setSelectedMsSchoolCompetencyManagementIDs([]);
                            }
                          },
                          tooltip: {
                            callbacks: {
                              title: (items) => {
                                const idx = items[0]?.dataIndex;
                                if (idx === undefined) return '';
                                const slot = chartData.datasetMeta?.[idx];
                                if (!slot) return '';
                                return `${slot.schoolName} · ${categoryMap[slot.category].label}`;
                              },
                              label: (context) => {
                                const rawLabel = context.dataset.label || '';
                                const buLabel = rawLabel.startsWith('__')
                                  ? rawLabel.slice(2)
                                  : rawLabel;
                                const value = (context.parsed as any)?.y ?? context.parsed ?? 0;
                                return `${buLabel}: ${value}`;
                              }
                            }
                          }
                        },
                        scales: {
                          x: {
                            stacked: true,
                            ticks: {
                              maxRotation: 0,
                              autoSkip: false,
                              color: '#6B7280',
                              font: { size: 11 }
                            }
                          },
                          y: {
                            stacked: true,
                            beginAtZero: true,
                            ticks: {
                              precision: 0
                            }
                          }
                        }
                      }}
                    />
                  ) : (
                    <Flex justify='center' align='center' h='100%'>
                      <Text c='dimmed'>{`No school overview data found for ${year}.`}</Text>
                    </Flex>
                  )}
                </Box>

                <Stack gap='sm'>
                  <Text fz='sm' fw={600}>
                    Branched Masterplan Detail
                  </Text>
                  <Text fz='sm' c='dimmed'>
                    {selectedSchool
                      ? `Showing ${
                          selectedCategory ? categoryMap[selectedCategory].label : 'all statuses'
                        } masterplans for ${selectedSchool}`
                      : 'Select a school bar above to load branched masterplan details.'}
                  </Text>
                  <Card withBorder px={0} py={0}>
                    {branchedLoading ? (
                      <Stack p='md'>
                        {Array.from({ length: 6 }).map((_, idx) => (
                          <Skeleton key={idx} height={32} radius='sm' />
                        ))}
                      </Stack>
                    ) : (
                      <DataTables
                        columns={detailColumns}
                        data={selectedSchool ? detailRows : []}
                        loading={branchedLoading}
                        withPagination
                        isNumbering
                        emptyMessage={
                          selectedSchool
                            ? `No data available for ${selectedSchool}.`
                            : 'Select a school above to load data.'
                        }
                      />
                    )}
                  </Card>
                </Stack>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value='courses' pt='md'>
              <Stack gap='md'>
                <Text c='dimmed' fz='sm'>
                  Click a status slice to view course details for that status.
                </Text>
                <Box h={360}>
                  {courseOverviewLoading ? (
                    <Flex justify='center' align='center' h='100%'>
                      <Loader />
                    </Flex>
                  ) : courseChartData.labels?.length ? (
                    <Pie
                      ref={courseChartRef as any}
                      data={courseChartData as any}
                      plugins={[pieSliceLabelPlugin]}
                      options={{
                        onClick: (event, elements) =>
                          handleCourseBarClick(event as ChartEvent, elements as ActiveElement[]),
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'right',
                            onClick: (event, legendItem, legend) => {
                              const chart = legend.chart;
                              const index = legendItem.index;
                              if (typeof index !== 'number') return;
                              const meta = chart.getDatasetMeta(0);
                              const arc = meta?.data?.[index] as { hidden?: boolean } | undefined;

                              if (!arc) return;

                              const willHide = arc.hidden !== true;
                              arc.hidden = willHide;
                              chart.update();

                              const status = courseChartData.labels?.[index] as
                                | MasterPlanOverviewCourseStatus
                                | undefined;

                              if (!status) return;
                              if (willHide && selectedCourseStatus === status) {
                                setSelectedCourseStatus(null);
                                return;
                              }

                              if (!willHide) {
                                setSelectedCourseStatus(status);
                              }
                            },
                            labels: {
                              generateLabels: (chart) => {
                                const data = chart.data;
                                const labels = data.labels || [];
                                const dataset = data.datasets?.[0];
                                const counts = (dataset?.data as number[]) || [];
                                const backgroundColors = Array.isArray(dataset?.backgroundColor)
                                  ? (dataset.backgroundColor as string[])
                                  : [];
                                const meta = chart.getDatasetMeta(0);

                                return labels
                                  .map((label, index) => {
                                    const value = counts[index] || 0;
                                    if (value <= 0) return null;

                                    const arc = meta?.data?.[index] as
                                      | { hidden?: boolean }
                                      | undefined;
                                    const hidden = arc?.hidden === true;
                                    const fillStyle = backgroundColors[index] || '#E5E7EB';
                                    const statusLabel = label as MasterPlanOverviewCourseStatus;
                                    const isSelected = selectedCourseStatus === statusLabel;

                                    return {
                                      text: `${label} (${value})`,
                                      fillStyle: hidden ? '#E5E7EB' : fillStyle,
                                      strokeStyle: isSelected ? '#111827' : '#fff',
                                      lineWidth: isSelected ? 2 : 1,
                                      hidden,
                                      index
                                    };
                                  })
                                  .filter(Boolean) as any;
                              }
                            }
                          },
                          tooltip: {
                            callbacks: {
                              label: (context) => {
                                const label = context.label || '';
                                const value = (context.parsed as any) ?? 0;
                                return `${label}: ${value}`;
                              }
                            }
                          }
                        }
                      }}
                    />
                  ) : (
                    <Flex justify='center' align='center' h='100%'>
                      <Text c='dimmed'>No course overview data found for {year}.</Text>
                    </Flex>
                  )}
                </Box>

                <Stack gap='sm'>
                  <Text fz='sm' fw={600}>
                    Course Detail
                  </Text>
                  <Text fz='sm' c='dimmed'>
                    {selectedCourseStatus
                      ? `Showing courses with status ${selectedCourseStatus}`
                      : 'Select a status slice above to load course details.'}
                  </Text>
                  <Card withBorder px={0} py={0}>
                    {courseOverviewLoading ? (
                      <Stack p='md'>
                        {Array.from({ length: 6 }).map((_, idx) => (
                          <Skeleton key={idx} height={32} radius='sm' />
                        ))}
                      </Stack>
                    ) : !selectedCourseStatus || groupedCourseRows.length === 0 ? (
                      <Flex justify='center' align='center' p='xl'>
                        <Text c='dimmed' fz='sm'>
                          {selectedCourseStatus
                            ? `No course data available for status ${selectedCourseStatus}.`
                            : 'Select a status slice above to load data.'}
                        </Text>
                      </Flex>
                    ) : (
                      <Box style={{ overflowX: 'auto' }}>
                        <Table striped highlightOnHover withColumnBorders>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>No</Table.Th>
                              <Table.Th>Status</Table.Th>
                              <Table.Th>Course Name</Table.Th>
                              <Table.Th>School</Table.Th>
                              <Table.Th>Expired Date</Table.Th>
                              <Table.Th>Summary Masterplan</Table.Th>
                              <Table.Th>Masterplan</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {groupedCourseRows.map((group, groupIdx) => {
                              return group.rows.map((row, rowIdx) => (
                                <Table.Tr key={`${group.courseName}-${rowIdx}`}>
                                  {rowIdx === 0 && (
                                    <>
                                      <Table.Td rowSpan={group.rows.length}>
                                        {groupIdx + 1}
                                      </Table.Td>
                                      <Table.Td rowSpan={group.rows.length}>
                                        {row.statusLabel}
                                      </Table.Td>
                                      <Table.Td rowSpan={group.rows.length}>
                                        {row.courseName}
                                      </Table.Td>
                                    </>
                                  )}
                                  <Table.Td>{row.schoolName}</Table.Td>
                                  <Table.Td>{row.expiredDate}</Table.Td>
                                  <Table.Td>
                                    {row.summaryMasterplan?.length ? (
                                      <Group gap='xs' wrap='wrap'>
                                        {row.summaryMasterplan.map((item) => (
                                          <Badge
                                            key={item.label}
                                            color={item.color}
                                            variant='light'
                                          >
                                            {item.label}: {item.count}
                                          </Badge>
                                        ))}
                                      </Group>
                                    ) : (
                                      <Text fz='sm' c='dimmed'>
                                        No mapping
                                      </Text>
                                    )}
                                  </Table.Td>
                                  <Table.Td>
                                    <Button
                                      size='xs'
                                      variant='light'
                                      onClick={() => {
                                        setSelectedCourseCompetencies(row.competencyName || []);
                                        setCourseCompetencyModalOpen(true);
                                      }}
                                      disabled={
                                        !row.competencyName || row.competencyName.length === 0
                                      }
                                    >
                                      {row.competencyName?.length
                                        ? 'View Masterplan'
                                        : 'No Competency'}
                                    </Button>
                                  </Table.Td>
                                </Table.Tr>
                              ));
                            })}
                          </Table.Tbody>
                        </Table>
                      </Box>
                    )}
                  </Card>
                </Stack>
              </Stack>
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Card>

      <Modal
        opened={competencyModalOpen}
        onClose={() => setCompetencyModalOpen(false)}
        title={modalTitle}
        size='lg'
        centered
      >
        {selectedCompetencies.length === 0 ? (
          <Text c='dimmed' fz='sm'>
            No competencies available.
          </Text>
        ) : (
          <Table striped highlightOnHover withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Competency Name</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Category</Table.Th>
                <Table.Th>Level</Table.Th>
                <Table.Th>Job Band</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {selectedCompetencies.map((competency) => (
                <Table.Tr key={competency.msCompetencyManagementID}>
                  <Table.Td>{competency.competencyName}</Table.Td>
                  <Table.Td>{competency.competencyType}</Table.Td>
                  <Table.Td>{competency.category}</Table.Td>
                  <Table.Td>{competency.level}</Table.Td>
                  <Table.Td>{competency.managementLevel}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Modal>

      <Modal
        opened={courseCompetencyModalOpen}
        onClose={() => setCourseCompetencyModalOpen(false)}
        title={courseCompetencyModalTitle}
        size='2xl'
        centered
      >
        {!selectedCourseCompetencies.length ? (
          <Text c='dimmed' fz='sm'>
            No competencies available.
          </Text>
        ) : (
          <Table striped highlightOnHover withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                {/* <Table.Th>Competency</Table.Th> */}
                <Table.Th>Masterplan</Table.Th>
                {/* <Table.Th>Job Family Group</Table.Th> */}
                {/* <Table.Th>Job Fam</Table.Th> */}
                {/* <Table.Th>Job Band</Table.Th> */}
                <Table.Th>Start Year</Table.Th>
                <Table.Th>End Year</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Action</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {selectedCourseCompetencies.flatMap((competency, idx) => {
                const hasMasterplan = Boolean(competency.listMasterplan?.length);
                if (!hasMasterplan) {
                  return (
                    <Table.Tr key={`${competency.competencyName}-${idx}`}>
                      <Table.Td>{competency.competencyName}</Table.Td>
                      <Table.Td>-</Table.Td>
                      <Table.Td>
                        <Badge variant='light' color='gray'>
                          No mapping
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  );
                }

                return competency.listMasterplan!.map((mp, mpIdx) => {
                  const status = mapMasterplanStatus(mp.statusMasterplan);
                  return (
                    <Table.Tr key={`${competency.competencyName}-${idx}-${mpIdx}`}>
                      {/* <Table.Td>{competency.competencyName}</Table.Td> */}
                      <Table.Td>{mp.masterplanName || '-'}</Table.Td>
                      {/* <Table.Td>{mp.jobFamGroup || '-'}</Table.Td> */}
                      {/* <Table.Td>{mp.jobFam || '-'}</Table.Td> */}
                      {/* <Table.Td>{mp.jobBand || '-'}</Table.Td> */}
                      <Table.Td>{mp.startYear || '-'}</Table.Td>
                      <Table.Td>{mp.endYear || '-'}</Table.Td>
                      <Table.Td>
                        <Badge variant='light' color={status.color}>
                          {status.label}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Button size='xs' variant='light' onClick={() => handleViewMasterplan(mp)}>
                          View Masterplan
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  );
                });
              })}
            </Table.Tbody>
          </Table>
        )}
      </Modal>
    </Stack>
  );
};

export default MasterplanOverview;
